/**
 * Équipe et pointage, adossés à Supabase.
 *
 * Un point de vigilance domine ce fichier : `employee_costs` est réservée au
 * patron par une policy. Pour tous les autres rôles la jointure ne ramène
 * simplement **rien** — pas une erreur, pas un refus, une absence. C'est le
 * comportement voulu, mais il oblige à ne jamais confondre « coût inconnu » et
 * « coût nul » : `hourlyCost` reste `null`, et l'écran le dit.
 */
import type {
  Employee,
  ExecutionContext,
  Invitation,
  RecordTimeCommand,
  Role,
  SaveEmployeeCommand,
  TeamRepository,
  TimeEntry,
  TimeEntryKind,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const num = (v: unknown): number => Number(v ?? 0);

const EMPLOYEE_COLUMNS = `
  id, user_id, first_name, last_name, job_title, phone,
  weekly_hours, hired_on, is_active,
  cost:employee_costs(hourly_cost)
`;

const ENTRY_COLUMNS = `
  id, employee_id, project_id, work_date, hours, kind, hourly_cost, overtime_premium, notes,
  project:projects(name)
`;

function toEmployee(row: Row): Employee {
  // La jointure remonte un tableau ou un objet selon la cardinalité déduite
  // par PostgREST, et un tableau vide quand la policy a filtré la ligne.
  const cost = Array.isArray(row.cost) ? row.cost[0] : row.cost;
  return {
    id: row.id,
    firstName: row.first_name ?? "",
    lastName: row.last_name ?? "",
    jobTitle: row.job_title ?? null,
    phone: row.phone ?? null,
    weeklyHours: num(row.weekly_hours) || 35,
    hiredOn: row.hired_on ?? null,
    isActive: row.is_active !== false,
    userId: row.user_id ?? null,
    // Surtout pas `?? 0` : un coût absent parce qu'on n'a pas le droit de le
    // lire deviendrait une heure gratuite, donc une marge fausse et flatteuse.
    hourlyCost: cost?.hourly_cost == null ? null : Number(cost.hourly_cost),
  };
}

function toInvitation(row: Row): Invitation {
  // `code_hash` n'est volontairement pas projeté. Il ne sert à rien ici — un
  // condensat ne se retourne pas en code — et ce qu'on ne fait pas descendre
  // dans le navigateur ne peut pas s'échapper d'un journal ou d'un cache.
  return {
    id: row.id,
    employeeId: row.employee_id ?? null,
    role: row.role as Role,
    expiresAt: row.expires_at,
    claimedAt: row.claimed_at ?? null,
    revokedAt: row.revoked_at ?? null,
    createdAt: row.created_at,
  };
}

function toEntry(row: Row): TimeEntry {
  const project = Array.isArray(row.project) ? row.project[0] : row.project;
  return {
    id: row.id,
    employeeId: row.employee_id,
    projectId: row.project_id ?? null,
    projectName: project?.name ?? null,
    workDate: String(row.work_date).slice(0, 10),
    hours: num(row.hours),
    kind: (row.kind ?? "work") as TimeEntryKind,
    hourlyCost: row.hourly_cost == null ? null : Number(row.hourly_cost),
    overtimePremium: Number(row.overtime_premium ?? 0),
    notes: row.notes ?? null,
  };
}

function fail(error: { message: string; code?: string }): never {
  // La borne des 24 heures et la policy sur les salaires remontent ici. Leur
  // message vient de la base et il est déjà en français : le remplacer par un
  // texte générique ferait perdre la seule information utile.
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseTeamRepository(
  clientFor: DbClientFactory,
): TeamRepository {
  return {
    async listEmployees(ctx: ExecutionContext, includeInactive: boolean) {
      let query = clientFor(ctx)
        .from("employees")
        .select(EMPLOYEE_COLUMNS)
        .order("last_name");

      if (!includeInactive) query = query.eq("is_active", true);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toEmployee);
    },

    async saveEmployee(ctx: ExecutionContext, command: SaveEmployeeCommand) {
      const patch = {
        first_name: command.firstName,
        last_name: command.lastName,
        job_title: command.jobTitle,
        phone: command.phone,
        weekly_hours: command.weeklyHours,
        hired_on: command.hiredOn,
      };

      // `tenant_id` n'est pas transmis : la colonne le dérive de la session,
      // et la policy WITH CHECK le vérifie.
      const query = command.employeeId
        ? clientFor(ctx).from("employees").update(patch).eq("id", command.employeeId)
        : clientFor(ctx).from("employees").insert(patch);

      const { data, error } = await query.select(EMPLOYEE_COLUMNS).single();
      if (error) fail(error);
      return toEmployee(data as Row);
    },

    async archiveEmployee(ctx: ExecutionContext, employeeId: string) {
      // Archivé, jamais supprimé : les heures d'un salarié parti restent dans
      // le coût des chantiers qu'il a faits, et dans le registre légal.
      const { error } = await clientFor(ctx)
        .from("employees")
        .update({ is_active: false })
        .eq("id", employeeId);
      if (error) fail(error);
    },

    async setHourlyCost(ctx: ExecutionContext, employeeId: string, cost: number) {
      const { error } = await clientFor(ctx)
        .from("employee_costs")
        .upsert(
          { employee_id: employeeId, hourly_cost: cost, updated_at: new Date().toISOString() },
          { onConflict: "employee_id" },
        );
      if (error) fail(error);
    },

    async listInvitations(ctx: ExecutionContext) {
      const { data, error } = await clientFor(ctx)
        .from("user_invitations")
        .select("id, employee_id, role, expires_at, claimed_at, revoked_at, created_at")
        .order("created_at", { ascending: false });
      if (error) fail(error);
      return (data ?? []).map(toInvitation);
    },

    async createInvitation(
      ctx: ExecutionContext,
      command: { employeeId: string | null; role: Role },
    ) {
      // Une RPC, et pas un `insert` : c'est la fonction qui tire le code et
      // n'en stocke que le condensat. Laisser l'insertion au client, ce serait
      // le laisser choisir le condensat — donc choisir le code, donc s'en
      // fabriquer un pour lui-même.
      const { data, error } = await clientFor(ctx).rpc("create_user_invitation", {
        p_employee_id: command.employeeId,
        p_role: command.role,
      });
      if (error) fail(error);
      // La seule fois où le code existe hors du condensat. L'appelant doit le
      // montrer sur-le-champ ; personne ne saura le relire.
      return String(data);
    },

    async revokeInvitation(ctx: ExecutionContext, invitationId: string) {
      const { error } = await clientFor(ctx).rpc("revoke_user_invitation", {
        p_invitation_id: invitationId,
      });
      if (error) fail(error);
    },

    async revokeAccess(ctx: ExecutionContext, employeeId: string) {
      const { error } = await clientFor(ctx).rpc("revoke_employee_access", {
        p_employee_id: employeeId,
      });
      if (error) fail(error);
    },

    async listEntries(ctx, range) {
      let query = clientFor(ctx)
        .from("time_entries")
        .select(ENTRY_COLUMNS)
        .gte("work_date", range.from)
        .lte("work_date", range.to)
        .order("work_date");

      if (range.employeeId) query = query.eq("employee_id", range.employeeId);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toEntry);
    },

    async recordTime(ctx: ExecutionContext, command: RecordTimeCommand) {
      // `hourly_cost` n'est **pas** transmis : le trigger `freeze_cost` le
      // recopie depuis `employee_costs` en SECURITY DEFINER. C'est ce qui
      // permet au conducteur de pointer sans avoir le droit de lire les
      // salaires — et l'envoyer d'ici supposerait qu'il les connaisse.
      const { data, error } = await clientFor(ctx)
        .from("time_entries")
        .insert({
          employee_id: command.employeeId,
          project_id: command.projectId,
          work_date: command.workDate,
          hours: command.hours,
          kind: command.kind,
          notes: command.notes,
        })
        .select(ENTRY_COLUMNS)
        .single();

      if (error) fail(error);
      return toEntry(data as Row);
    },

    async deleteTime(ctx: ExecutionContext, entryId: string) {
      const { error } = await clientFor(ctx)
        .from("time_entries")
        .delete()
        .eq("id", entryId);
      if (error) fail(error);
    },
  };
}
